export const COMPANY = {
  name: "Hadley Energy Solutions",
  phone: "(502) 921-3777",
  phoneTel: "+1-502-921-3777",
  email: "info@hadleyenergysolutions.com",
  careersEmail: "career@hadleyenergysolutions.com",
  address: "965 Old Preston Hwy N, Louisville, KY 40229",
  street: "965 Old Preston Hwy N",
  city: "Louisville",
  state: "KY",
  zip: "40229",
  founded: "2013",
  licenses: "CE64623 | ME64500 | HM06538",
  owner: "Rodney Hadley",
  ownerTitle: "Master Electrician, Certified Commercial Energy Auditor",
  tagline: "The Electrical, HVAC & Energy Specialists",
  url: "https://hadleyenergysolutions.com",
  facebook: "https://www.facebook.com/people/Hadley-Energy-Solutions/100063256869073/",
  linkedin: "https://www.linkedin.com/company/hadleyenergysolutions",
  googleReviews: "https://share.google/wAzK0RXCbRzvte5d0",
  hours: "Mon–Fri 8:00 AM – 4:30 PM",
  hoursWeekend: "Sat–Sun Closed | Emergency service available 24/7",
  paymentMethods: "Check, Venmo",
  certifications: "Certified Energy Auditing Company — recognized by Energy Audit Institute and Energy Efficiency Association",
  associations: "Associated Builders and Contractors of Indiana/Kentucky, Energy Efficiency Association",
  brandPartners: "Distech Controls, EcoStruxure, Generac",
  warranty: "1-year warranty on all work · Manufacturer's warranties available",
};

/* ── Shared sub-objects ── */

const addressObj = {
  "@type": "PostalAddress" as const,
  streetAddress: COMPANY.street,
  addressLocality: COMPANY.city,
  addressRegion: COMPANY.state,
  postalCode: COMPANY.zip,
  addressCountry: "US",
};

const geoObj = {
  "@type": "GeoCoordinates" as const,
  latitude: 38.0689,
  longitude: -85.6719,
};

const logoObj = {
  "@type": "ImageObject" as const,
  url: `${COMPANY.url}/images/hadley-logo.png`,
  width: 1428,
  height: 883,
};

const areaServedArr = [
  { "@type": "City" as const, name: "Louisville", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Elizabethtown", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Lexington", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Shepherdsville", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Mt. Washington", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Jeffersontown", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "New Albany", containedInPlace: { "@type": "State" as const, name: "Indiana" } },
  { "@type": "City" as const, name: "Jeffersonville", containedInPlace: { "@type": "State" as const, name: "Indiana" } },
  { "@type": "City" as const, name: "Clarksville", containedInPlace: { "@type": "State" as const, name: "Indiana" } },
  { "@type": "City" as const, name: "Bardstown", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "La Grange", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Frankfort", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Bowling Green", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Florence", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Covington", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
  { "@type": "City" as const, name: "Hillview", containedInPlace: { "@type": "State" as const, name: "Kentucky" } },
];

/* ── Organization (parent corporate entity) ──
 *
 * Distinct from the LocalBusiness so search engines and AI assistants can
 * resolve the "company" identity separately from the physical storefront.
 * The LocalBusiness references this via `parentOrganization`.
 */

const ORG_ID = `${COMPANY.url}/#organization`;
const LOCALBUSINESS_ID = `${COMPANY.url}/#localbusiness`;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: COMPANY.name,
    legalName: COMPANY.name,
    url: COMPANY.url,
    logo: logoObj,
    image: `${COMPANY.url}/images/og-image.jpg`,
    description: "Family-owned electrical, HVAC, and energy management contractor serving Louisville, KY and Southern Indiana since 2013.",
    foundingDate: COMPANY.founded,
    founder: { "@type": "Person", name: COMPANY.owner, jobTitle: COMPANY.ownerTitle },
    email: COMPANY.email,
    telephone: COMPANY.phone,
    address: addressObj,
    sameAs: [COMPANY.facebook, COMPANY.linkedin],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: COMPANY.phone,
        contactType: "customer service",
        areaServed: ["US-KY", "US-IN"],
        availableLanguage: "English",
      },
      {
        "@type": "ContactPoint",
        email: COMPANY.careersEmail,
        contactType: "human resources",
        availableLanguage: "English",
      },
    ],
  };
}

/* ── Electrician (LocalBusiness) — physical storefront entity ── */

// Pre-encoded Google Maps query for the HQ address — used as `hasMap` so
// search engines and AI assistants can link the LocalBusiness to a verifiable
// map location at 965 Old Preston Hwy N, Louisville, KY 40229.
const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  `${COMPANY.street}, ${COMPANY.city}, ${COMPANY.state} ${COMPANY.zip}`
)}`;

export function electricianSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "Electrician", "HVACBusiness"],
    "@id": LOCALBUSINESS_ID,
    name: COMPANY.name,
    legalName: COMPANY.name,
    url: COMPANY.url,
    telephone: COMPANY.phone,
    email: COMPANY.email,
    description: "Licensed electrical contractor and HVAC company serving Louisville KY and Southern Indiana since 2013. Commercial, industrial, and residential services.",
    image: `${COMPANY.url}/images/og-image.jpg`,
    logo: logoObj,
    priceRange: "$$",
    paymentAccepted: COMPANY.paymentMethods,
    currenciesAccepted: "USD",
    address: addressObj,
    geo: geoObj,
    hasMap: mapUrl,
    foundingDate: COMPANY.founded,
    founder: { "@type": "Person", name: COMPANY.owner },
    parentOrganization: { "@id": ORG_ID },
    numberOfEmployees: { "@type": "QuantitativeValue", minValue: 10, maxValue: 50 },
    sameAs: [COMPANY.facebook, COMPANY.linkedin],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: COMPANY.phone,
      contactType: "customer service",
      availableLanguage: "English",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "16:30",
      },
    ],
    areaServed: areaServedArr,
    hasCredential: [
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "license",
        recognizedBy: { "@type": "Organization", name: "Kentucky DHBC" },
        name: "CE64623",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "license",
        recognizedBy: { "@type": "Organization", name: "Kentucky DHBC" },
        name: "ME64500",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "license",
        recognizedBy: { "@type": "Organization", name: "Kentucky DHBC" },
        name: "HM06538",
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Electrical, HVAC & Energy Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Electrical Service & Repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Commercial Electrical Contracting" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Industrial Electrical Contracting" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "24/7 Emergency HVAC Repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Commercial HVAC Systems" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Residential HVAC Services" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Commercial Lighting Design" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Commercial LED Retrofit" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Energy Audit Services" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Guaranteed Energy Savings Financing" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Transformer Installation" } },
      ],
    },
    // NOTE: aggregateRating is intentionally OMITTED sitewide. A hardcoded
    // rating/review count drifts out of date and marking up numbers we cannot
    // substantiate on-page is a structured-data risk. Google pulls the live
    // rating from the Google Business Profile for local results. Do not re-add.
  };
}


/* ── Backward-compat alias ── */
export const localBusinessSchema = electricianSchema;

/* ── Global schemas — separate blocks (no @graph) ── */

export function globalSchemas() {
  return [organizationSchema(), electricianSchema()];
}

/* ── JobPosting ──
 *
 * Emits one schema.org JobPosting per open role. `datePosted` and `validThrough`
 * use a rolling 30-day window so postings stay valid in Google for Jobs without
 * a manual refresh.
 */

export interface JobPostingInput {
  title: string;
  description: string;
  employmentType?: "FULL_TIME" | "PART_TIME" | "CONTRACTOR" | "TEMPORARY" | "INTERN" | "VOLUNTEER" | "PER_DIEM" | "OTHER";
  category?: string;
}

export function jobPostingSchema(job: JobPostingInput) {
  const now = new Date();
  const posted = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const validThrough = new Date(now.getFullYear(), now.getMonth() + 3, 0).toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    datePosted: posted,
    validThrough,
    employmentType: job.employmentType || "FULL_TIME",
    industry: "Electrical & HVAC Contracting",
    occupationalCategory: job.category || job.title,
    directApply: false,
    hiringOrganization: {
      "@type": "Organization",
      "@id": ORG_ID,
      name: COMPANY.name,
      sameAs: COMPANY.url,
      logo: `${COMPANY.url}/images/hadley-logo.png`,
    },
    jobLocation: {
      "@type": "Place",
      address: addressObj,
    },
    
    applicantLocationRequirements: [
      { "@type": "Country", name: "US" },
    ],
    applicationContact: {
      "@type": "ContactPoint",
      email: COMPANY.careersEmail,
      telephone: COMPANY.phone,
      contactType: "Hiring",
    },
  };
}


/* ── BreadcrumbList ──
 *
 * Emits a schema.org BreadcrumbList with:
 *  - A stable `@id` on the list itself (canonical page URL + #breadcrumb), so
 *    other JSON-LD blocks (e.g. WebPage, Service) can reference it.
 *  - A stable `@id` on every ListItem (page URL + #breadcrumb-N).
 *  - For non-tail items: `item` is the absolute URL string (Google's preferred
 *    shape for parent crumbs).
 *  - For the final/tail item: `item` is a `{ "@type": "WebPage", "@id": URL }`
 *    object so the leaf still exposes its canonical URL — required for Google
 *    to resolve parent/child relationships when validating breadcrumbs.
 *
 * `items` must be ordered Home → … → current page. Each `url` is a site-root
 * path (e.g. "/blog/some-slug"); the COMPANY.url origin is prepended here.
 */
export function breadcrumbSchema(items: { name: string; url: string }[]) {
  if (!items || items.length === 0) return null;
  const tail = items[items.length - 1]!;
  const tailUrl = `${COMPANY.url}${tail.url}`;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${tailUrl}#breadcrumb`,
    itemListElement: items.map((item, i) => {
      const isLast = i === items.length - 1;
      const absUrl = `${COMPANY.url}${item.url}`;
      const entry: Record<string, unknown> = {
        "@type": "ListItem",
        "@id": `${tailUrl}#breadcrumb-${i + 1}`,
        position: i + 1,
        name: item.name,
      };
      if (isLast) {
        entry['item'] = { "@type": "WebPage", "@id": absUrl, url: absUrl, name: item.name };
      } else {
        entry['item'] = absUrl;
      }
      return entry;
    }),
  };
}

/* ── FAQPage ── */

export function faqSchema(faqs: { question: string; answer: string }[]) {
  if (!faqs || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

/* ── Service ── */

export function serviceSchema(service: {
  name: string;
  description: string;
  url: string;
  areaServed?: string[];
  category?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.category || service.name,
    name: service.name,
    description: service.description,
    url: `${COMPANY.url}${service.url}`,
    provider: {
      "@type": "Electrician",
      "@id": `${COMPANY.url}/#localbusiness`,
      name: COMPANY.name,
      url: COMPANY.url,
      telephone: COMPANY.phone,
      image: `${COMPANY.url}/images/og-image.jpg`,
      address: addressObj,
      geo: geoObj,
      priceRange: "$$",
    },
    areaServed: (service.areaServed || [
      "Louisville, KY", "Elizabethtown, KY", "Lexington, KY",
      "Shepherdsville, KY", "Mt. Washington, KY", "Jeffersontown, KY",
      "New Albany, IN", "Jeffersonville, IN", "Clarksville, IN",
    ]).map(area => ({ "@type": "City", name: area })),
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      description: "Free estimates available",
    },
  };
}

/* ── Review schema intentionally removed ──
   Self-serving reviews are excluded from rich results, and the anonymous
   Electrician node (no @id) duplicated the canonical #localbusiness entity. */

